"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_WORKLOG_RULES = exports.WORKLOG_UNITS = void 0;
exports.roundToUnit = roundToUnit;
exports.withinMaxPerDay = withinMaxPerDay;
exports.withinBackdateWindow = withinBackdateWindow;
exports.isMonthLocked = isMonthLocked;
exports.earliestWritableDay = earliestWritableDay;
const dates_1 = require("../dates");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C13 — the four worklog rules, as pure functions.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 1802–1829 and `WS_WORKLOG`. The mockup's four: **unit**, **maximum per day**,
 * **backdating window**, **monthly lock day**.
 *
 * ⚠️⚠️ **HERE RATHER THAN IN THE SERVICE, BECAUSE ALL FOUR HAVE TO BE ASKED IN TWO PLACES.**
 * The server is the only enforcement that counts (CLAUDE.md rule 6), but a screen that cannot say
 * *"this would exceed the daily maximum"* until the request comes back makes somebody type an entry
 * twice. One implementation, asked by both — and unit tests over arithmetic rather than over a
 * screen.
 *
 * ⚠️ **The mockup's own words matter here and are quoted at each rule**, because three of the four
 * have an edge the wording settles.
 */
/**
 * The rounding unit.
 *
 * `min15` / `min30` are the mockup's `'15'` / `'30'`; `decimal` is its `'dec'`.
 */
exports.WORKLOG_UNITS = ['min15', 'min30', 'decimal'];
/** The mockup's `WS_WORKLOG`, which is what a workspace starts with. */
exports.DEFAULT_WORKLOG_RULES = {
    unit: 'min15',
    maxPerDay: 12,
    backdateDays: 14,
    backdateNoLimit: false,
    lockDay: 5,
};
/* ── 1. the unit ──────────────────────────────────────────────────────────────────────────── */
/**
 * Round an amount of hours to the workspace's unit.
 *
 * The mockup: *"Minden bejegyzés a legközelebbi {unit} percre kerekül"* — every entry rounds to the
 * nearest N minutes — and for `decimal`, *"a bevitt idő tizedes órában tárolódik, megjelenítéskor
 * H:MM alakra kerekítjük"*.
 *
 * ⚠️⚠️ **ROUNDING NEVER PRODUCES ZERO, AND THAT IS NOT WHAT "NEAREST" SAYS.** Five minutes to the
 * nearest quarter hour is zero — which would turn a deliberate entry into `hours = 0`, and 0017's
 * `check (hours > 0)` would then refuse a row the person had every right to expect. Refusing is
 * worse than the alternative: somebody logged *something*, so the smallest thing this workspace can
 * represent is one unit. **Rounds to nearest, floored at one unit.**
 *
 * ⚠️ `decimal` still rounds to two places, because the column is `numeric(6,2)`. Storing
 * `0.333333` and reading back `0.33` would make a screen disagree with the database about a figure
 * somebody bills from.
 */
function roundToUnit(hours, unit) {
    if (unit === 'decimal')
        return Math.max(0.01, Math.round(hours * 100) / 100);
    const minutes = unit === 'min15' ? 15 : 30;
    const step = minutes / 60;
    const rounded = Math.round(hours / step) * step;
    /* One unit, never zero. See above. */
    return Math.max(step, Math.round(rounded * 100) / 100);
}
/**
 * Would this amount push the day over the workspace's maximum?
 *
 * The mockup: *"E fölött a rögzítés hibát dob, a napi összeg nem menthető"* — above this the entry
 * throws and the day's total cannot be saved.
 *
 * ⚠️⚠️ **`alreadyLogged` MUST EXCLUDE THE ROW BEING EDITED, and the caller is the only one who can
 * know which that is.** Editing an 8-hour entry down to 6 with a 12-hour maximum has to compare
 * 6 against the day's *other* entries — counting the 8 as well makes lowering a figure fail, which
 * is the sort of refusal that reads as a bug in the maximum rather than in the check.
 */
function withinMaxPerDay(hours, alreadyLogged, rules) {
    const total = alreadyLogged + hours;
    /* ⚠️ A tolerance, because these are sums of two-decimal values: 12.000000000000002 > 12 is true
     * in floating point and false in every sense that matters. Half a minute is well below the
     * smallest unit anything here can store. */
    if (total <= rules.maxPerDay + 0.005)
        return { ok: true };
    return { ok: false, total, max: rules.maxPerDay };
}
/**
 * May this member log against this day, given how far back the workspace allows?
 *
 * The mockup: *"Ennél régebbi napra csak admin tud rögzíteni"* — only an admin may log further
 * back — and with no limit, *"Bármikorra lehet visszamenőleg rögzíteni — a zárónap ettől még
 * érvényes"*: **the lock day still applies.** The two rules are independent and both are checked.
 *
 * ⚠️ **`mayOverride` is the caller's decision, not this function's.** The service passes whether the
 * member holds `worklog.lock` — the only existing action that means "administers time" — rather than
 * this module inventing a permission name. See `worklog.service.ts`.
 *
 * ⚠️ **The window is INCLUSIVE and counted in days, so `backdateDays: 14` means the last fourteen
 * days including today.** The alternative reading (fourteen days *before* today) makes the setting
 * off by one against every calendar somebody counts on their fingers.
 */
function withinBackdateWindow(workedOn, today, rules, mayOverride) {
    if (rules.backdateNoLimit || mayOverride)
        return { ok: true };
    const earliest = (0, dates_1.addWireDays)(today, -(rules.backdateDays - 1)) ?? today;
    /* Wire dates compare as strings — `dates.ts` has the argument. */
    if (workedOn >= earliest)
        return { ok: true };
    return { ok: false, earliest, days: rules.backdateDays };
}
/* ── 4. the monthly lock ──────────────────────────────────────────────────────────────────── */
/**
 * Is the month containing `workedOn` closed, as of `today`?
 *
 * The mockup: *"Minden hónap {lockDay}. napja után az előző hónap lezárul: ott sem módosítani, sem
 * új worklogot rögzíteni nem lehet."*
 *
 * ⚠️⚠️ **"AFTER THE Nth" MEANS THE Nth IS STILL OPEN.** On the 5th with `lockDay: 5`, the previous
 * month is not yet closed; on the 6th it is. Reading it as `>=` steals a day from everybody every
 * month, and it is the kind of off-by-one nobody reports because they assume they misremembered the
 * deadline.
 *
 * ⚠️⚠️ **AND IT LOCKS EVERY MONTH BEFORE THE PREVIOUS ONE UNCONDITIONALLY.** The rule reads as
 * *"the previous month closes"*, but a month that closed in March is not open again in August — so
 * anything earlier than the still-open boundary is locked whatever the day of the month is. Reading
 * it as *only* the previous month would leave every older month editable forever, which is the
 * opposite of what a monthly close is for.
 */
function isMonthLocked(workedOn, today, lockDay) {
    if (lockDay === null)
        return false;
    const now = (0, dates_1.fromWireDate)(today);
    const target = (0, dates_1.fromWireDate)(workedOn);
    if (now === null || target === null)
        return false;
    /**
     * The first day of the earliest month that is still open.
     *
     * Before the lock day, the previous month is still open, so the boundary is the *previous*
     * month's first day. From the day after the lock day, the boundary is this month's first day.
     */
    const boundaryMonth = now.getDate() > lockDay ? now.getMonth() : now.getMonth() - 1;
    const boundary = new Date(now.getFullYear(), boundaryMonth, 1);
    /* ⚠️ Built from local parts so a year boundary normalises itself — January minus one month is
     * December of the previous year, which `new Date(y, -1, 1)` gets right and arithmetic on a wire
     * string does not. */
    return target < boundary;
}
/**
 * The earliest day a write is allowed, all four rules considered — for a message, not a check.
 *
 * ⚠️ **`null` when nothing constrains it.** A screen that renders "you may log from {date}" needs
 * to know when there is no such date, rather than being handed today and implying a limit.
 */
function earliestWritableDay(today, rules, mayOverride) {
    const bounds = [];
    if (!rules.backdateNoLimit && !mayOverride) {
        const earliest = (0, dates_1.addWireDays)(today, -(rules.backdateDays - 1));
        if (earliest !== null)
            bounds.push(earliest);
    }
    if (rules.lockDay !== null) {
        const now = (0, dates_1.fromWireDate)(today);
        if (now !== null) {
            const month = now.getDate() > rules.lockDay ? now.getMonth() : now.getMonth() - 1;
            const boundary = new Date(now.getFullYear(), month, 1);
            const year = String(boundary.getFullYear());
            const monthPart = String(boundary.getMonth() + 1).padStart(2, '0');
            bounds.push(`${year}-${monthPart}-01`);
        }
    }
    if (bounds.length === 0)
        return null;
    /* The tightest of them — the latest date, since these are all lower bounds. */
    return bounds.reduce((latest, candidate) => (candidate > latest ? candidate : latest));
}
//# sourceMappingURL=rules.js.map